/**
 * WhisperTranscriptionService — Client-side service for OpenAI Whisper transcription
 *
 * Sends audio blobs to the /api/transcribe Vercel serverless function,
 * which forwards them to OpenAI Whisper API for accurate post-session transcription.
 *
 * This replaces the Web Speech API transcript as the canonical record after a session ends.
 * Web Speech API continues to provide free real-time captions during the session.
 */

import { TranscriptionSegment, TranscriptionSession } from './TranscriptionService';
import { firebaseAuthService } from './firebase/FirebaseAuthService';

export interface WhisperTranscriptionResult {
  text: string;
  segments: WhisperSegment[];
  language: string;
  duration: number;
  sessionId: string;
}

interface WhisperSegment {
  id: number;
  start: number;
  end: number;
  text: string;
}

export type WhisperStatus = 'idle' | 'uploading' | 'transcribing' | 'complete' | 'error';

class WhisperTranscriptionService {
  private readonly API_ENDPOINT = '/api/transcribe';
  private status: WhisperStatus = 'idle';

  /**
   * Transcribe an audio blob using OpenAI Whisper (via server-side API).
   *
   * Sends the original WebM/Opus audio blob (~1-3MB per hour) to the server.
   * Returns a TranscriptionSession compatible with the rest of the app.
   *
   * @param audioBlob - Audio blob from MediaRecorder (WebM/Opus preferred)
   * @param sessionId - Session identifier for correlation
   * @param options - Optional language and status callback
   * @returns TranscriptionSession or null on failure
   */
  async transcribe(
    audioBlob: Blob,
    sessionId: string,
    options?: {
      language?: string;
      onStatusChange?: (status: WhisperStatus) => void;
    }
  ): Promise<TranscriptionSession | null> {
    const onStatusChange = options?.onStatusChange;
    this.updateStatus('uploading', onStatusChange);

    try {
      // Get Firebase auth token for server-side verification
      const token = await firebaseAuthService.getIdToken();
      if (!token) {
        throw new Error('Not authenticated — cannot transcribe');
      }

      // Build FormData with audio file
      const formData = new FormData();
      const extension = audioBlob.type.includes('webm') ? 'webm' : 'wav';
      formData.append('audio', audioBlob, `session_${sessionId}.${extension}`);
      formData.append('sessionId', sessionId);
      formData.append('language', options?.language || 'en');

      this.updateStatus('transcribing', onStatusChange);

      const response = await fetch(this.API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // DO NOT set Content-Type — browser sets it with multipart boundary
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Transcription failed: ${response.status}`);
      }

      const result: WhisperTranscriptionResult = await response.json();
      this.updateStatus('complete', onStatusChange);

      // Map Whisper result to existing TranscriptionSession interface
      return this.mapToTranscriptionSession(result, sessionId);
    } catch (error) {
      console.error('Whisper transcription failed:', error);
      this.updateStatus('error', onStatusChange);
      return null;
    }
  }

  /**
   * Map Whisper API response to the app's TranscriptionSession interface.
   * This ensures compatibility with all existing consumers (export, history, notes editor).
   */
  private mapToTranscriptionSession(
    result: WhisperTranscriptionResult,
    sessionId: string
  ): TranscriptionSession {
    const segments: TranscriptionSegment[] = result.segments.map((seg, index) => ({
      id: `whisper_${index}`,
      text: seg.text.trim(),
      confidence: 0.95, // Whisper does not expose per-segment confidence; default high
      timestamp: seg.start * 1000, // Convert seconds to milliseconds
      isFinal: true,
      speaker: undefined, // Whisper-1 does not do speaker diarization
      duration: seg.end - seg.start,
    }));

    return {
      sessionId,
      segments,
      fullText: result.text,
      startTime: new Date(),
      language: result.language || 'en-US',
      totalWords: result.text.split(/\s+/).filter(w => w.length > 0).length,
      avgConfidence: 0.95,
    };
  }

  /**
   * Get the current processing status.
   */
  getStatus(): WhisperStatus {
    return this.status;
  }

  private updateStatus(
    status: WhisperStatus,
    callback?: (status: WhisperStatus) => void
  ): void {
    this.status = status;
    callback?.(status);
  }
}

export const whisperTranscriptionService = new WhisperTranscriptionService();
